'use client'

import { useMemo } from 'react'
import Container from '@/components/structure/Container'
import Section from '@/components/content/common/Section'
import TabToggle from '@/components/navigation/Toggles'
import AppRanking, { AppRankingTitle } from '@/components/content/app/AppRanking'
import DealsTrending from '@/components/content/deals/root/DealsTrending'
import Tree from '@/components/content/sidebar/Tree'
import StackSidebar from '@/components/content/stacks/stack/StackSidebar'
import RichText from '@/components/content/common/RichText'
import RelatedComparisons from '@/components/content/common/RelatedComparisons'
import ExploreButton from '@/components/content/common/ExploreButton'
import Meta from '@/components/structure/Meta'
import StackHero from '@/components/content/stacks/stack/hero/StackHero'
import Videos from '@/components/content/videos/Videos'
import ComparisonThumbnail from '@/components/content/comparisons/root/ComparisonThumbnail'
import ReviewThumbnail from '@/components/content/reviews/root/ReviewThumbnail'
import Timestamp from '@/components/ui/Timestamp'
import Authors from '@/components/content/common/Authors'
import PreferredSource from '@/components/ui/PreferredSource'
import { DealOverview, DealEligibility } from '@/components/content/deals/deal/DealSummary'
import DealClaimButton from '@/components/content/deals/DealClaimButton'
import Pill from '@/components/ui/Pill'
import { FeaturedTagType } from '@/collections/FeaturedTag/constants'
import { usePageContext, PageContextProvider } from '@/components/content/common/PageContext'
import { Icon } from '@/icons/Icon'
import { isPopulatedObject, toSlug } from '@/lib/utils'
import { extractBlogSlugUrlFromFaq } from '@/lib/formatting'
import {
  convertAppToAppCategoryRecommendation,
  orderAppsByRecommendation,
} from '@/components/content/best/helpers'
import {
  buildStackCategoryGroupBuckets,
  type CategoryGroupMapping,
} from '@/components/content/common/categoryGroupBuckets'
import type { SectionItem, Block } from '@/types/app'
import type {
  Stack,
  Faq,
  StacksFaq,
  Category,
  App,
  AppCategoryRecommendation,
  Deal,
  Video,
  VideoAppsApp,
  Comparison,
} from '@/payload-types'

type StackContainerProps = {
  stack: Stack
  apps: App[]
  deals: (Deal & { appId: App['id'] })[]
  videoEntries?: { video: Video; videoApps: VideoAppsApp[] }[]
  category: Category | null
  categoryGroupLookup: Record<number, CategoryGroupMapping>
  comparisons: Comparison[]
  modifiedTimestamp: string
}

export default function StackContainer(props: StackContainerProps) {
  return (
    <PageContextProvider scrollOffset={145} initialTab="Apps Stack">
      <StackContainerContent {...props} />
    </PageContextProvider>
  )
}

function StackContainerContent({
  stack,
  apps,
  deals: serverDeals,
  videoEntries,
  category,
  categoryGroupLookup,
  comparisons: serverComparisons,
  modifiedTimestamp,
}: StackContainerProps) {
  const videos = useMemo(() => videoEntries?.map((e) => e.video), [videoEntries])
  const videoAppsMap = useMemo(
    () => Object.fromEntries((videoEntries ?? []).map((e) => [e.video.id, e.videoApps])),
    [videoEntries],
  )
  const { pageTab } = usePageContext()

  const faqs = useMemo(
    () =>
      (stack.stackFaqs?.docs || [])
        .filter((doc) => isPopulatedObject<StacksFaq>(doc))
        .map((doc) => doc.faq)
        .filter((faq) => isPopulatedObject<Faq>(faq)),
    [stack.stackFaqs?.docs],
  )

  const appCategoryRecommendations = useMemo(
    () =>
      apps
        .map((app) => convertAppToAppCategoryRecommendation(app, category?.id))
        .filter((rec) => isPopulatedObject<AppCategoryRecommendation>(rec)),
    [apps, category?.id],
  )

  const { orderedAppRecs, secondaryApps } = useMemo(
    () => orderAppsByRecommendation(appCategoryRecommendations),
    [appCategoryRecommendations],
  )

  const categoryGroupSections = useMemo(
    () =>
      buildStackCategoryGroupBuckets(apps, categoryGroupLookup).map((bucket) => {
        const { orderedAppRecs: groupedOrderedAppRecs, secondaryApps: groupedSecondaryApps } =
          orderAppsByRecommendation(bucket.appCategoryRecommendations)

        return {
          bucket,
          orderedAppRecs: groupedOrderedAppRecs,
          secondaryApps: groupedSecondaryApps,
        }
      }),
    [apps, categoryGroupLookup],
  )
  const hasGroupedCategorySections = categoryGroupSections.length > 0
  const summary = stack.summary?.root?.children

  const createdAt = stack.createdAt
  const appById = useMemo(() => new Map(apps.map((app) => [app.id, app])), [apps])

  // Summary
  const summarySection = [
    {
      title: 'Summary',
      content: (
        <div className="flex flex-col gap-6">
          {summary && <RichText content={summary as Block[]} />}
          {videos && videos.length > 0 && <Videos videos={videos} videoAppsMap={videoAppsMap} />}

          {hasGroupedCategorySections ? (
            <div className="flex flex-col gap-6">
              {categoryGroupSections.map(({ bucket, orderedAppRecs, secondaryApps }) => {
                return (
                  <AppRanking
                    key={bucket.id}
                    apps={orderedAppRecs}
                    otherApps={secondaryApps}
                    category={bucket.category}
                    theme="light"
                    rank="none"
                    title={
                      <AppRankingTitle
                        title={bucket.title}
                        icon={bucket.category.icon}
                        theme="light"
                        tag="h2"
                      />
                    }
                    showCategoryBadge={bucket.scope === 'categoryGroup'}
                    className="sm:gap-5"
                  />
                )
              })}
            </div>
          ) : (
            category && (
              <AppRanking
                apps={orderedAppRecs}
                otherApps={secondaryApps}
                category={category}
                theme="light"
                rank="none"
                title={<AppRankingTitle title={category.name} icon="magnifier" theme="light" />}
                className="sm:gap-5"
              />
            )
          )}
        </div>
      ),
    },
  ]

  // Notes
  const notes = stack.notes?.root?.children
  const notesSection = notes
    ? [
        {
          title: `Final Notes`,
          content: <RichText content={notes as Block[]} />,
        },
      ]
    : []

  // FAQ
  const faqSection: SectionItem[] = [
    {
      title: 'FAQ',
      content: (
        <div className="flex flex-col gap-4">
          {faqs.map((section, sectionIndex) => {
            return (
              <Section
                key={`faq-${sectionIndex}`}
                sectionId={`faq-${toSlug(section.question ?? 'Untitled')}`}
                title={section.question ?? 'Untitled'}
                isChild={true}
                icon={<Icon name="circle-question" />}
                collapsed={sectionIndex > 0}
                renderCollapsedContent={true}
              >
                {section.answer?.root?.children ? (
                  <RichText
                    content={section.answer.root.children as Block[]}
                    readMore={extractBlogSlugUrlFromFaq(section)}
                  />
                ) : (
                  <p>No answer available</p>
                )}
              </Section>
            )
          })}
        </div>
      ),
    },
  ]

  const filteredDeals = useMemo(
    () => serverDeals.filter((deal) => appById.has(deal.appId)),
    [serverDeals, appById],
  )
  const dealsSection: SectionItem[] = useMemo(() => {
    return filteredDeals.flatMap((deal): SectionItem[] => {
      const app = appById.get(deal.appId)
      if (!app) return []

      const link = app.slug ? `/deals/${app.slug}` : '/deals'

      return [
        {
          title: deal.name ?? 'Untitled',
          content: (
            <div className="flex w-full flex-col gap-4 rounded-xl border border-gray-200 p-4 dark:border-white/10">
              <ReviewThumbnail reviewApp={app} hideButton={true} customLink={link} />
              <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/10 xs:flex-row xs:items-center">
                <div className="flex flex-col lg:flex-row lg:items-center lg:gap-3">
                  <DealOverview deal={deal} invert={true} />
                  <DealEligibility
                    eligibility={deal.eligibility}
                    terms={deal.terms}
                    invert={true}
                    hideInfo={true}
                  />
                </div>
                <DealClaimButton deal={deal} app={app} className="xs:ml-auto">
                  <Pill style="outlinePrimary">Claim</Pill>
                </DealClaimButton>
              </div>
            </div>
          ),
        },
      ]
    })
  }, [filteredDeals, appById])
  const totalDeals = filteredDeals.length

  const comparisonsSection: SectionItem[] = useMemo(
    () =>
      serverComparisons.map((comparison) => ({
        title: comparison.name ?? 'Untitled',
        content: (
          <div className="flex flex-col gap-4">
            <RichText content={(comparison.tldr?.root?.children as Block[]) ?? []} />
            <ComparisonThumbnail comparison={comparison} type={FeaturedTagType.ThumbnailPrimary} />
          </div>
        ),
      })),
    [serverComparisons],
  )
  const totalComparisons = serverComparisons.length

  // Structure
  const content: SectionItem[] = (() => {
    switch (pageTab) {
      case 'Apps Stack':
        return [...summarySection, ...notesSection, ...(faqs.length > 0 ? faqSection : [])]
      case 'Deals':
        return [...dealsSection]
      case 'Comparison':
        return [...comparisonsSection]
      default:
        return []
    }
  })()

  return (
    <>
      <Meta modifiedTimestamp={modifiedTimestamp} publishedTimestamp={stack.createdAt} ftc={true} />
      <StackHero stack={stack} apps={apps} />

      <article id="content" className="overlay expanded">
        <Container
          sidebar={
            <>
              <Tree roots={content} header={<StackSidebar stack={stack} apps={apps} />} />
              <PreferredSource invert={true} />
              <div className="hidden flex-wrap items-center gap-y-2.5 md:flex">
                {(modifiedTimestamp || createdAt) && (
                  <Timestamp
                    modified={modifiedTimestamp}
                    published={createdAt}
                    lead="Last updated on"
                    trail="by"
                    className="pr-1"
                  />
                )}
                <Authors condensed={true} />
              </div>
              <DealsTrending />
            </>
          }
          mobilePadding={false}
        >
          <TabToggle
            defaultValue="Apps Stack"
            defaultIcon={<Icon name="stack" />}
            deals={totalDeals}
            comparisonCount={totalComparisons}
          />

          {content.map((section, index) => (
            <Section
              key={index}
              title={section.title || ''}
              isParent={section.title === 'FAQ' || (section.children?.length ?? 0) > 0}
            >
              {section.content}
            </Section>
          ))}
        </Container>

        <Container reduced={true}>
          <RelatedComparisons comparisons={serverComparisons} />
          <ExploreButton label="Explore all stacks" link="/stacks" />
        </Container>
      </article>
    </>
  )
}
